/**
 * update-deps command — (re-)install all required Admin dependencies.
 * Safe to run repeatedly; the package manager will skip already-installed packages.
 */

import path from 'node:path'
import * as clack from '@clack/prompts'
import type { UpdateDepsCommandOptions } from '@core-engine/commands/runtime.js'
import { detectPackageManager } from '@core-engine/utils/package-manager.js'
import { spinner } from '@core-engine/utils/spinner.js'
import { resolveConfig } from '@next-adapter/config.js'
import {
  getCliDependencySyncPlan,
  getDependencyPlan,
  installDependenciesAsync
} from '@next-adapter/init/scaffolders/dependencies.js'
import { getInstalledIntegrations } from '@next-adapter/integration-runtime.js'
import { getInstalledPresets } from '@next-adapter/preset-runtime.js'

export async function runUpdateDepsCommand(options: UpdateDepsCommandOptions): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()

  clack.intro('BetterStart Update Dependencies')

  const pm = detectPackageManager(cwd)
  clack.log.info(`Package manager: ${pm}`)

  const config = await resolveConfig(cwd)
  const dependencyPlan = getDependencyPlan(
    getInstalledPresets(config),
    getInstalledIntegrations(config),
    false
  )
  const cliDependencyPlan = getCliDependencySyncPlan(cwd)

  const s = spinner()
  s.start('Installing dependencies...')

  const result = await installDependenciesAsync({
    cwd,
    pm,
    dependencies: Array.from(
      new Set([...dependencyPlan.dependencies, ...(cliDependencyPlan?.dependencies ?? [])])
    ),
    devDependencies: Array.from(
      new Set([...dependencyPlan.devDependencies, ...(cliDependencyPlan?.devDependencies ?? [])])
    )
  })

  if (result.success) {
    s.stop(`Installed ${result.dependencies.length} deps + ${result.devDeps.length} dev deps`)
  } else {
    s.stop('Dependency install failed')
    clack.log.error(result.error ?? 'Unknown error')
    process.exit(1)
  }

  clack.outro('Dependencies updated')
}
